import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import FilePreviewModal from '../components/FilePreviewModal';

// ==================== DROP ZONE UPLOAD ====================
const DropZone = ({ folderId, onUploaded, driveUrl, compact = false }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleUpload = async (file) => {
    setUploading(true); setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/drive/upload-to-folder/${folderId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult({ success: true, name: r.data.filename });
      setTimeout(() => { setUploadResult(null); onUploaded && onUploaded(); }, 1500);
    } catch (err) {
      setUploadResult({ success: false, name: err.response?.data?.detail || 'Erreur' });
    } finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); };
  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onFileSelect = (e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); };

  return (
    <div className={`${compact ? 'p-3 px-4' : 'p-5'} border-2 border-dashed rounded-lg ${dragging ? 'border-[#008A8D] bg-[#008A8D]/5' : 'border-gray-200 bg-gray-50/50'} transition-colors`}
      onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave} data-testid="dropzone">
      {uploading ? (
        <div className="flex items-center justify-center gap-2 py-2 text-sm text-[#008A8D]">
          <div className="w-4 h-4 border-2 border-t-[#008A8D] border-gray-200 rounded-full animate-spin"></div>
          Envoi en cours...
        </div>
      ) : uploadResult ? (
        <div className={`text-center py-2 text-sm font-medium ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
          {uploadResult.success ? `${uploadResult.name} ajoute avec succes` : uploadResult.name}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            <span>Glissez un fichier ici ou</span>
            <button onClick={() => fileInputRef.current?.click()} className="text-[#008A8D] font-medium hover:underline" data-testid="dropzone-browse">parcourir</button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={onFileSelect} />
          </div>
          {driveUrl && <a href={driveUrl} target="_blank" rel="noreferrer" className="text-[#008A8D] text-xs hover:underline shrink-0">Ouvrir dans Drive</a>}
        </div>
      )}
    </div>
  );
};

// ==================== FLYERS PANEL ====================
const FlyersPanel = () => {
  const [downloading, setDownloading] = useState(null);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const url = kind === 'prospect' ? `${API}/marketing/flyer/prospect.pdf` : `${API}/marketing/flyer/welcome.pdf`;
      const r = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = kind === 'prospect' ? 'RealEase_Flyer_Prospect.pdf' : 'RealEase_Flyer_Bienvenue_Client.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert('Erreur telechargement : ' + (e.response?.data?.detail || e.message));
    } finally { setDownloading(null); }
  };

  return (
    <div className="space-y-3" data-testid="flyers-panel">
      <h3 className="font-semibold text-gray-700 mb-2 text-sm">Supports marketing</h3>
      <p className="text-xs text-gray-500 mb-3">Flyers A6 imprimables (avec fond perdu 3mm et traits de coupe) - a remettre lors de rendez-vous, salons ou en piece jointe d'email.</p>

      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#008A8D] transition-colors">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#13736D] to-[#0F5C57] rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-[#ECC19B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900">Flyer Prospect</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">Persuasif : promesse autofinance + badge 100% + 6 etapes + QR pre-qualification portail.</p>
          </div>
        </div>
        <button onClick={() => download('prospect')}
                disabled={downloading === 'prospect'}
                className="mt-3 w-full px-3 py-2 bg-[#008A8D] text-white text-xs font-semibold rounded-md hover:bg-[#006B6D] disabled:opacity-50"
                data-testid="download-flyer-prospect">
          {downloading === 'prospect' ? 'Generation...' : 'Telecharger le PDF'}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#008A8D] transition-colors">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#ECC19B] to-[#E0A878] rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900">Flyer Bienvenue Client</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">Pour clients signes : 6 etapes du parcours, badge 100% autofinance et QR vers portail client.</p>
          </div>
        </div>
        <button onClick={() => download('welcome')}
                disabled={downloading === 'welcome'}
                className="mt-3 w-full px-3 py-2 bg-[#E0A878] text-white text-xs font-semibold rounded-md hover:bg-[#cf9863] disabled:opacity-50"
                data-testid="download-flyer-welcome">
          {downloading === 'welcome' ? 'Generation...' : 'Telecharger le PDF'}
        </button>
      </div>

      <div className="text-[10px] text-gray-400 italic mt-2 leading-relaxed px-1">
        Astuce : les flyers sont regeneres a chaque clic avec les logos officiels et bleed d'impression integres.
      </div>
    </div>
  );
};

// ==================== DOCUMENTS PAGE ====================
const DocumentsPage = () => {
  const { user } = useAuth();
  const canEdit = user && user.role !== 'client';
  const [tab, setTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [clientFolders, setClientFolders] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [structLoading, setStructLoading] = useState(false);
  const [path, setPath] = useState([]);
  const [contents, setContents] = useState(null);
  const [contentsLoading, setContentsLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {}),
      axios.get(`${API}/drive/clients/list`).then(r => setClientFolders(r.data.items || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const loadStructure = async (projectId) => {
    setSelectedProject(projectId);
    setStructLoading(true);
    setPath([]); setContents(null);
    try {
      const r = await axios.get(`${API}/drive/structure/${projectId}`);
      setStructure(r.data);
    } catch { setStructure(null); }
    finally { setStructLoading(false); }
  };

  const loadContents = async (folderId) => {
    setContentsLoading(true);
    try {
      const r = await axios.get(`${API}/drive/contents/${folderId}`, { params: { project_id: selectedProject } });
      setContents(r.data);
    } catch { setContents(null); }
    finally { setContentsLoading(false); }
  };

  const openTopFolder = (folder) => {
    const folderId = folder.folder_id;
    setPath([{ folder_id: folderId, name: folder.name, url: folder.drive_url }]);
    loadContents(folderId);
  };

  const openSubfolder = (sub) => {
    setPath(prev => [...prev, { folder_id: sub.folder_id, name: sub.name, url: sub.url }]);
    loadContents(sub.folder_id);
  };

  const goToBreadcrumb = (index) => {
    const newPath = path.slice(0, index + 1);
    setPath(newPath);
    loadContents(newPath[newPath.length - 1].folder_id);
  };

  const goBackToRoot = () => {
    setPath([]); setContents(null);
  };

  const handleCreateFolder = async () => {
    const parent = path[path.length - 1];
    if (!newFolderName.trim() || !parent) return;
    setCreatingFolder(true);
    try {
      await axios.post(`${API}/drive/folder`, { parent_id: parent.folder_id, name: newFolderName.trim() });
      setNewFolderName(''); setShowCreateInput(false);
      loadContents(parent.folder_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally { setCreatingFolder(false); }
  };

  const handleRenameFolder = async (sub) => {
    const newName = window.prompt('Nouveau nom du dossier :', sub.name);
    if (!newName || newName.trim() === sub.name) return;
    try {
      await axios.patch(`${API}/drive/folder/${sub.folder_id}`, { name: newName.trim() });
      const parent = path[path.length - 1];
      loadContents(parent.folder_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du renommage');
    }
  };

  const handleDeleteFolder = async (sub) => {
    if (!window.confirm(`Supprimer le dossier "${sub.name}" et tout son contenu ?`)) return;
    try {
      await axios.delete(`${API}/drive/folder/${sub.folder_id}`);
      const parent = path[path.length - 1];
      loadContents(parent.folder_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const topFolderWithIds = useMemo(() => {
    if (!structure) return [];
    return structure.folders.map(f => {
      let folderId = null;
      if (f.drive_url) {
        const m = f.drive_url.match(/folders\/([^?&/]+)/);
        if (m) folderId = m[1];
      }
      return { ...f, folder_id: folderId };
    });
  }, [structure]);

  const folderIcons = {
    '01_Admin': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    '02_Banque': 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    '06_Photos_Videos': 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  };
  const defaultIcon = 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z';

  const currentFolderId = path.length > 0 ? path[path.length - 1].folder_id : null;
  const currentFolderUrl = path.length > 0 ? path[path.length - 1].url : (structure?.root_url || '');

  return (
    <div className="p-4 lg:p-8" data-testid="documents-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
        <p className="text-gray-600 mt-1">Fichiers synchronises depuis Google Drive</p>
        {structure?.root_url && (
          <a href={structure.root_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm text-[#008A8D] hover:underline">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
            Ouvrir le dossier racine dans Drive
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="space-y-2">
          <div className="flex bg-gray-100 rounded-lg p-1 mb-3" data-testid="docs-tab-switcher">
            <button onClick={() => setTab('projects')}
              className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === 'projects' ? 'bg-white text-[#008A8D] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="docs-tab-projects">Projets</button>
            <button onClick={() => setTab('clients')}
              className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === 'clients' ? 'bg-white text-[#008A8D] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="docs-tab-clients">Clients ({clientFolders.length})</button>
            <button onClick={() => setTab('flyers')}
              className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === 'flyers' ? 'bg-white text-[#008A8D] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="docs-tab-flyers">Flyers</button>
          </div>

          {loading ? (
            <div className="w-6 h-6 border-3 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div>
          ) : tab === 'flyers' ? (
            <FlyersPanel />
          ) : tab === 'projects' ? (
            <>
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Dossiers Projet</h3>
              {projects.length === 0 && <p className="text-xs text-gray-400 italic">Aucun projet</p>}
              {projects.map(p => (
                <button key={p.project_id} onClick={() => loadStructure(p.project_id)}
                  className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${selectedProject === p.project_id ? 'bg-[#008A8D] text-white' : 'bg-white border border-gray-200 hover:border-[#008A8D]'}`}
                  data-testid={`doc-project-${p.project_id}`}>
                  <p className="font-medium truncate">{p.name}</p>
                  <p className={`text-xs ${selectedProject === p.project_id ? 'text-white/70' : 'text-gray-500'}`}>{p.client_name}</p>
                </button>
              ))}
            </>
          ) : (
            <>
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Dossiers Client (Drive)</h3>
              {clientFolders.length === 0 && <p className="text-xs text-gray-400 italic">Aucun dossier client cree</p>}
              {clientFolders.map(cf => {
                const admin = cf.subfolders?.['Doc admins clients'];
                const projets = cf.subfolders?.['Projets'];
                return (
                  <div key={cf.lead_id} className="bg-white border border-gray-200 rounded-lg p-3 text-sm" data-testid={`client-folder-${cf.lead_id}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <svg className="w-5 h-5 text-[#A57F4F] mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                      <p className="font-semibold text-gray-900 truncate flex-1">{cf.folder_name}</p>
                    </div>
                    <div className="space-y-1 pl-1">
                      {cf.root_url && (
                        <a href={cf.root_url} target="_blank" rel="noreferrer" className="block text-xs text-[#008A8D] hover:underline truncate" data-testid={`client-root-${cf.lead_id}`}>
                          Ouvrir le dossier racine
                        </a>
                      )}
                      {admin?.url && (
                        <a href={admin.url} target="_blank" rel="noreferrer" className="block text-xs text-gray-600 hover:text-[#008A8D] truncate">{'> Doc admins clients'}</a>
                      )}
                      {projets?.url && (
                        <a href={projets.url} target="_blank" rel="noreferrer" className="block text-xs text-gray-600 hover:text-[#008A8D] truncate">{'> Projets'}</a>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="lg:col-span-3">
          {!selectedProject ? (
            <div className="text-center py-16 text-gray-400">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
              <p>Selectionnez un projet pour voir ses documents</p>
            </div>
          ) : structLoading ? (
            <div className="text-center py-16"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div><p className="text-gray-500 mt-3 text-sm">Chargement des dossiers Drive...</p></div>
          ) : !structure ? (
            <div className="text-center py-16 text-gray-400">Erreur de chargement</div>
          ) : path.length === 0 ? (
            <div>
              <h3 className="text-lg font-semibold mb-4">{structure.project_name}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {topFolderWithIds.map(folder => (
                  <button key={folder.name} onClick={() => folder.folder_id && openTopFolder(folder)}
                    disabled={!folder.folder_id}
                    className="realease-card p-5 flex items-center gap-4 hover:border-[#008A8D] hover:shadow-md transition-all text-left disabled:opacity-50"
                    data-testid={`top-folder-${folder.name}`}>
                    <div className="w-12 h-12 rounded-lg bg-[#008A8D]/10 flex items-center justify-center shrink-0">
                      <svg className="w-6 h-6 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d={folderIcons[folder.name] || defaultIcon} /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{folder.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{folder.count} fichier{folder.count !== 1 ? 's' : ''}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center flex-wrap gap-2 mb-5 text-sm" data-testid="breadcrumb">
                <button onClick={goBackToRoot} className="text-[#008A8D] hover:underline font-medium flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  {structure.project_name}
                </button>
                {path.map((p, i) => (
                  <React.Fragment key={p.folder_id}>
                    <span className="text-gray-300">/</span>
                    <button onClick={() => goToBreadcrumb(i)} className={`${i === path.length - 1 ? 'text-gray-900 font-semibold' : 'text-[#008A8D] hover:underline'}`}>{p.name}</button>
                  </React.Fragment>
                ))}
              </div>

              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {canEdit && (
                    showCreateInput ? (
                      <div className="flex items-center gap-2">
                        <input type="text" autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowCreateInput(false); setNewFolderName(''); } }}
                          placeholder="Nom du dossier" className="realease-input text-sm py-2 w-56" data-testid="new-folder-input" />
                        <button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()} className="realease-btn-primary text-sm py-2 px-3 disabled:opacity-50" data-testid="confirm-create-folder">{creatingFolder ? '...' : 'Creer'}</button>
                        <button onClick={() => { setShowCreateInput(false); setNewFolderName(''); }} className="text-sm text-gray-500 hover:text-gray-700">Annuler</button>
                      </div>
                    ) : (
                      <button onClick={() => setShowCreateInput(true)} className="realease-btn-secondary text-sm flex items-center gap-2" data-testid="create-folder-btn">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        Creer un dossier
                      </button>
                    )
                  )}
                </div>
                {currentFolderUrl && (
                  <a href={currentFolderUrl} target="_blank" rel="noreferrer" className="text-sm text-[#008A8D] hover:underline flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    Ouvrir dans Drive
                  </a>
                )}
              </div>

              {currentFolderId && canEdit && (
                <div className="mb-4">
                  <DropZone folderId={currentFolderId} onUploaded={() => loadContents(currentFolderId)} />
                </div>
              )}

              {contentsLoading ? (
                <div className="text-center py-10"><div className="w-6 h-6 border-3 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div>
              ) : contents ? (
                <div className="realease-card overflow-hidden">
                  {contents.subfolders.length === 0 && contents.files.length === 0 ? (
                    <p className="text-center py-10 text-gray-400 text-sm">Ce dossier est vide</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {contents.subfolders.map(sub => (
                        <div key={sub.folder_id} className="flex items-center justify-between p-3 px-5 hover:bg-gray-50 group" data-testid={`subfolder-${sub.name}`}>
                          <button onClick={() => openSubfolder(sub)} className="flex items-center gap-3 flex-1 text-left min-w-0">
                            <svg className="w-5 h-5 text-[#008A8D] shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                            <span className="font-medium text-gray-900 truncate">{sub.name}</span>
                          </button>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a href={sub.url} target="_blank" rel="noreferrer" className="text-xs text-[#008A8D] hover:underline">Drive</a>
                            {canEdit && (
                              <>
                                <button onClick={() => handleRenameFolder(sub)} className="text-xs text-gray-500 hover:text-gray-800" data-testid={`rename-folder-${sub.name}`} title="Renommer">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                </button>
                                <button onClick={() => handleDeleteFolder(sub)} className="text-xs text-red-500 hover:text-red-700" data-testid={`delete-folder-${sub.name}`} title="Supprimer">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V4a2 2 0 012-2h4a2 2 0 012 2v3" /></svg>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                      {contents.files.map(doc => (
                        <button key={doc.file_id} onClick={() => setPreviewFile(doc)}
                          className="w-full flex items-center justify-between p-3 px-5 hover:bg-gray-50 text-left" data-testid={`file-row-${doc.file_id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            {doc.icon ? <img src={doc.icon} alt="" className="w-5 h-5" /> :
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            }
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                              {doc.modified && <p className="text-xs text-gray-400">{new Date(doc.modified).toLocaleDateString('fr-FR')} {new Date(doc.modified).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}</p>}
                            </div>
                          </div>
                          <span className="text-[#008A8D] text-sm font-medium shrink-0">Apercu</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center py-10 text-gray-400 text-sm">Erreur de chargement du dossier</p>
              )}
            </div>
          )}
        </div>
      </div>
      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};

export default DocumentsPage;
